/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.signature.Signature
import com.ionspin.kotlin.crypto.signature.crypto_sign_SEEDBYTES
import kotlin.time.Instant
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.utools.now

/**
 * Secret key to sign only
 */
@Serializable
@SerialName("sigs")
class SigningSecretKey(
        override val keyBytes: UByteArray,
        @Transient
        private var cachedPublicKey: VerifyingPublicKey?=null
    ) : UniversalKey(), SigningKey {

    override val verifyingKey: VerifyingPublicKey by lazy {
        cachedPublicKey ?:
            VerifyingPublicKey(Signature.ed25519SkToPk(keyBytes)).also { cachedPublicKey = it }
    }

    override val magic: KeysmagicNumber = KeysmagicNumber.defaultSigningSecret

    override fun sign(message: UByteArray): UByteArray = Signature.detached(message, keyBytes)

    override fun seal(message: UByteArray, expiresAt: Instant?): Seal =
        Seal.create(this, message, now(), expiresAt)

    @Transient
    override val id: KeyId = verifyingKey.id

    override val label: String
        get() = "sig"

    /**
     * Create a [Multikey] that requires presence of this or [other] key
     */
    infix fun or(other: VerifyingPublicKey) = Multikey(this) or other
    /**
     * Create a [Multikey] that requires presence of this or [other] key
     */
    infix fun or(other: SigningSecretKey) = Multikey(this) or other
    /**
     * Create a [Multikey] that requires presence of this or [other] key
     */
    infix fun or(other: Multikey) = Multikey(this) or other

    /**
     * Create a [Multikey] that requires presence of this and [other] key
     */
    infix fun and(other: VerifyingPublicKey) = Multikey(this) and other
    /**
     * Create a [Multikey] that requires presence of this and [other] key
     */
    infix fun and(other: SigningSecretKey) = Multikey(this) and other
    /**
     * Create a [Multikey] that requires presence of this and [other] key
     */
    infix fun and(other: Multikey) = Multikey(this) and other

    companion object {

        data class SigningKeyPair(val secretKey: SigningSecretKey, val publicKey: VerifyingPublicKey)

        /**
         * Required seed size for deterministic signing key generation.
         */
        const val seedLength = crypto_sign_SEEDBYTES

        fun generatePair(): SigningKeyPair {
            val p = Signature.keypair()
            val publicKey = VerifyingPublicKey(p.publicKey)
            return SigningKeyPair(SigningSecretKey(p.secretKey, publicKey), publicKey)
        }

        /**
         * Generate the key pair deterministically from an exact [seedLength]-byte seed.
         */
        fun generatePair(seed: UByteArray): SigningKeyPair {
            require(seed.size == seedLength) { "seed should be $seedLength bytes" }
            val p = Signature.seedKeypair(seed)
            val publicKey = VerifyingPublicKey(p.publicKey)
            return SigningKeyPair(SigningSecretKey(p.secretKey, publicKey), publicKey)
        }

        fun new(): SigningSecretKey = generatePair().secretKey

        /**
         * Create a deterministic signing key from an exact [seedLength]-byte seed.
         */
        fun fromSeed(seed: UByteArray): SigningSecretKey = generatePair(seed).secretKey

        /**
         * Derive an exact [seedLength]-byte seed from arbitrary byte material.
         *
         * Use this only with high-entropy source bytes. It is not a password KDF.
         */
        fun seedFromBytes(source: UByteArray): UByteArray =
            KeySeed.fromBytes(source, seedLength, "SigningSecretKey")

    }
}
